import { useState, useEffect, useMemo } from "react";
import { View, Text, Dialog, Spinner, Button } from "./base";
import { Icon } from "./base/Icon";
import { useAppDispatch, useAppSelector } from "../redux";
import { getSessionTraceOverview, getSpanDetail, TraceSpan, SpanPreview } from "../redux/reducers/sessionTraceSlice";
import { SpanDetailView } from "./traces/SpanDetailView";
import { safeString } from "../utils/strings";

interface SessionTraceViewerProps {
  agentId?: string;
  sessionId: string;
}

type AnySpan = SpanPreview | TraceSpan;

const formatDuration = (ms: number): string => {
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3 });
};

const hasAttributes = (span: AnySpan): span is TraceSpan => {
  return "attributes" in span && span.attributes !== undefined;
};

const getVoicerunSpanType = (span: AnySpan): string | undefined => {
  if ("voicerunSpanType" in span && span.voicerunSpanType) return span.voicerunSpanType;
  if (hasAttributes(span)) {
    const value = span.attributes["voicerun.span.type"];
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
};

const getSpanKindInfo = (span: AnySpan) => {
  let spanKind: string | undefined;
  if ("openinferenceSpanKind" in span && span.openinferenceSpanKind) {
    spanKind = span.openinferenceSpanKind;
  } else if (hasAttributes(span)) {
    const spanKindValue = span.spanKind || span.attributes["openinference.span.kind"];
    spanKind = typeof spanKindValue === "string" ? spanKindValue : undefined;
  } else {
    spanKind = span.spanKind || undefined;
  }
  const isLLMSpan = spanKind === "LLM";
  const isToolSpan = spanKind === "TOOL";
  const isAgentSpan = spanKind?.toLowerCase() === "agent";
  return { spanKind, isLLMSpan, isToolSpan, isAgentSpan };
};

const getEventIcon = (eventName: string | null | undefined): string => {
  switch (eventName) {
    case "start":
      return "play";
    case "stop":
      return "stop";
    case "text":
      return "message";
    case "text_to_speech":
      return "volume-up";
    case "audio":
      return "music";
    case "timeout":
      return "clock";
    case "log":
      return "snippet";
    case "start_session":
      return "rocket";
    case "session_end":
      return "logout";
    case "error":
      return "error";
    case "silence":
      return "mute";
    case "interrupt":
    case "turn_interrupted":
      return "close";
    case "initialize":
      return "play";
    case "handler_end":
    case "turn_end":
      return "check";
    case "debug":
      return "code";
    case "testing":
      return "testing";
    case "context":
      return "info";
    case "stt_model_switched":
    case "stt_update_settings":
      return "settings";
    case "start_recording":
      return "mic";
    case "stop_recording":
      return "mute";
    case "connect_sts":
    case "update_call":
    case "transfer_session":
      return "phone";
    case "disconnect_sts":
      return "close";
    case "collect_payment":
    case "collect_payment_success":
      return "link";
    case "supervisor_request":
    case "supervisor_response":
      return "user";
    case "merge_session":
      return "link";
    case "transcript_part":
      return "snippet";
    case "input_allowed":
    case "dtmf":
      return "keyboard";
    case "external_event":
    case "custom_event":
      return "zap";
    default:
      return "link";
  }
};

const getSpanIcon = (span: AnySpan): string => {
  const voicerunSpanType = getVoicerunSpanType(span);
  if (voicerunSpanType === "logger") return "snippet";
  if (voicerunSpanType === "background") return "clock";
  const { isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  return isLLMSpan ? "sparkles" : isToolSpan ? "tool" : isAgentSpan ? "bot" : getEventIcon(span.eventName);
};

const getSpanIconColor = (span: AnySpan): string => {
  const voicerunSpanType = getVoicerunSpanType(span);
  if (voicerunSpanType === "logger") return "text-gray-500 dark:text-gray-400";
  if (voicerunSpanType === "background") return "text-purple-500 dark:text-purple-400";
  const { isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  if (isLLMSpan) return "text-indigo-500 dark:text-indigo-400";
  if (isToolSpan) return "text-amber-500 dark:text-amber-400";
  if (isAgentSpan) return "text-violet-500 dark:text-violet-400";
  switch (span.eventName) {
    case "stop":
    case "session_end":
      return "text-red-500 dark:text-red-400";
    case "start_session":
      return "text-orange-500 dark:text-orange-400";
    case "start":
      return "text-green-500 dark:text-green-400";
    case "text_to_speech":
    case "audio":
      return "text-blue-500 dark:text-blue-400";
    case "text":
      return "text-emerald-400 dark:text-emerald-300";
    case "timeout":
      return "text-orange-400 dark:text-orange-300";
    default:
      return "text-gray-400 dark:text-gray-500";
  }
};

const getSpanDisplayName = (span: AnySpan): string => {
  const { isAgentSpan } = getSpanKindInfo(span);
  if (isAgentSpan) return "Agent Session";
  const voicerunSpanType = getVoicerunSpanType(span);
  if (voicerunSpanType === "background") {
    const dashIndex = span.name.indexOf("-");
    return dashIndex !== -1 ? span.name.substring(0, dashIndex) : span.name;
  }
  return span.name;
};

const getSpanIconTooltip = (span: AnySpan): string => {
  const { spanKind, isLLMSpan, isToolSpan, isAgentSpan } = getSpanKindInfo(span);
  return isLLMSpan ? "LLM" : isToolSpan ? "TOOL" : isAgentSpan ? "agent" : span.eventName || spanKind || "unknown";
};

const getIsForceClosed = (span: AnySpan, isAgentSpan: boolean): boolean => {
  if (isAgentSpan) return false;
  if ("forceClosed" in span) return span.forceClosed === true;
  if (hasAttributes(span)) return span.attributes["voicerun.force_closed"] === true;
  return false;
};

const getIsInterrupted = (span: AnySpan, isAgentSpan: boolean): boolean => {
  if (isAgentSpan) return false;
  if ("interrupted" in span) return span.interrupted === true;
  if (hasAttributes(span)) return span.attributes["voicerun.interrupted"] === true;
  return false;
};

const SpanRow = ({
  span,
  depth = 0,
  isExpanded,
  onToggle,
  onSpanClick,
}: {
  span: AnySpan & { children?: AnySpan[] };
  depth?: number;
  isExpanded: boolean;
  onToggle: () => void;
  onSpanClick: (span: AnySpan) => void;
}) => {
  const hasChildren = span.children && span.children.length > 0;
  const paddingLeft = depth * 20 + 12;
  const { isAgentSpan } = getSpanKindInfo(span);
  const isForceClosed = getIsForceClosed(span, isAgentSpan);
  const isInterrupted = getIsInterrupted(span, isAgentSpan);
  const isLoggerError =
    getVoicerunSpanType(span) === "logger" &&
    ((hasAttributes(span) && typeof span.attributes["log.level"] === "string" && span.attributes["log.level"].toLowerCase() === "error") ||
      ("preview" in span && typeof span.preview === "string" && span.preview.toLowerCase().startsWith("[error]")));
  const isError = !isInterrupted && (span.statusCode?.toUpperCase() === "ERROR" || isLoggerError);

  const getPreviewContent = (): React.ReactNode | null => {
    if ("preview" in span && span.preview) {
      const previewStr = safeString(span.preview);
      return previewStr ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{previewStr}</Text> : null;
    }

    if (!hasAttributes(span)) return null;

    const attrs = span.attributes;
    try {
      const voicerunSpanType = attrs["voicerun.span.type"];
      if (voicerunSpanType === "logger") {
        const level = attrs["log.level"];
        const message = attrs["log.message"];
        if (typeof level === "string" && typeof message === "string") {
          const levelColors: Record<string, string> = {
            error: "text-red-500 dark:text-red-400",
            warn: "text-amber-500 dark:text-amber-400",
            debug: "text-gray-400 dark:text-gray-500",
            info: "text-blue-500 dark:text-blue-400",
          };
          const color = levelColors[level.toLowerCase()] || levelColors.info;
          return (
            <Text className="text-xs truncate">
              <span className={`font-mono font-medium ${color}`}>[{level.toUpperCase()}]</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1">{message}</span>
            </Text>
          );
        }
        return null;
      }

      switch (span.eventName) {
        case "text": {
          const inputValue = attrs["input.value"];
          if (typeof inputValue === "string") {
            const parsed = JSON.parse(inputValue);
            const text = safeString(parsed?.event?.data?.text);
            return text ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{text}</Text> : null;
          }
          return null;
        }
        case "text_to_speech": {
          const outputValue = attrs["output.value"];
          if (typeof outputValue === "string") {
            const parsed = JSON.parse(outputValue);
            const text = safeString(parsed?.data?.text);
            return text ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{text}</Text> : null;
          }
          return null;
        }
        case "log": {
          const outputValue = attrs["output.value"];
          if (typeof outputValue === "string") {
            const parsed = JSON.parse(outputValue);
            const text = safeString(parsed?.data?.message);
            return text ? <Text className="text-xs text-gray-500 dark:text-gray-400 truncate">{text}</Text> : null;
          }
          return null;
        }
        default:
          return null;
      }
    } catch {
      return null;
    }
  };
  const previewContent = getPreviewContent();

  const eventIcon = getSpanIcon(span);
  const eventTooltip = getSpanIconTooltip(span);
  const displayName = getSpanDisplayName(span);
  const iconColor = getSpanIconColor(span);

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleRowClick = () => {
    onSpanClick(span);
  };

  return (
    <View
      className={`border-b border-gray-100 dark:border-gray-700 transition-colors cursor-pointer ${isError ? "bg-red-50/50 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20" : "hover:bg-indigo-50 dark:hover:bg-indigo-900/20"}`}
      onClick={handleRowClick}
    >
      <View
        className="flex flex-row items-center py-2 px-3 gap-3"
        style={{ paddingLeft }}
      >
        {hasChildren ? (
          <button
            onClick={handleToggleClick}
            className="p-0.5 -m-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
          >
            <Icon
              name={isExpanded ? "chevron-down" : "chevron-right"}
              size={14}
              className="text-gray-400"
            />
          </button>
        ) : (
          <View className="w-[14px] flex-shrink-0" />
        )}

        <View className="flex-1 min-w-0 flex flex-row items-center gap-2">
          <View className="flex flex-row items-center gap-2 flex-shrink-0">
            <Icon
              name={eventIcon}
              size={14}
              title={eventTooltip}
              className={iconColor}
            />
            <Text className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{displayName}</Text>
            {isInterrupted && (
              <span
                className="text-xs font-medium text-orange-500 dark:text-orange-400 cursor-pointer"
                title="This span was interrupted before it could complete"
              >
                Interrupted
              </span>
            )}
          </View>
          {previewContent}
          {span.statusMessage && !isInterrupted && !previewContent && (
            <Text className="text-xs text-red-600 dark:text-red-400 truncate">{safeString(span.statusMessage)}</Text>
          )}
        </View>

        <View className="flex flex-row items-center gap-4 flex-shrink-0">
          <Text className="text-xs text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">{formatTimestamp(span.startTime)}</Text>
          <Text className="text-xs text-gray-700 dark:text-gray-300 font-mono w-20 text-right">{isForceClosed ? "" : formatDuration(span.durationMs)}</Text>
        </View>
      </View>
    </View>
  );
};

export const SessionTraceViewer = ({ agentId, sessionId }: SessionTraceViewerProps) => {
  const dispatch = useAppDispatch();
  const { currentTrace, isLoading, error, overviewSpans, spanDetailCache, loadingSpanId } = useAppSelector((state) => state.sessionTrace);
  const [expandedSpans, setExpandedSpans] = useState<Set<string>>(new Set());
  const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);

  useEffect(() => {
    if (agentId && sessionId) {
      dispatch(getSessionTraceOverview({ agentId, sessionId }));
    }
  }, [dispatch, agentId, sessionId]);

  const spans = overviewSpans || [];
  const traceSummary = currentTrace?.trace;

  const spanTree = useMemo(() => {
    if (spans.length === 0) return [];

    const spanMap = new Map<string, AnySpan & { children: AnySpan[] }>();
    const roots: (AnySpan & { children: AnySpan[] })[] = [];

    spans.forEach((span) => {
      spanMap.set(span.spanId, { ...span, children: [] });
    });

    spans.forEach((span) => {
      const spanWithChildren = spanMap.get(span.spanId)!;
      if (span.parentId && spanMap.has(span.parentId)) {
        spanMap.get(span.parentId)!.children.push(spanWithChildren);
      } else {
        roots.push(spanWithChildren);
      }
    });

    const sortSpans = (items: (AnySpan & { children: AnySpan[] })[]) => {
      items.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      items.forEach((span) => sortSpans(span.children as (AnySpan & { children: AnySpan[] })[]));
    };
    sortSpans(roots);

    return roots;
  }, [spans]);

  useEffect(() => {
    if (spanTree.length > 0) {
      const allSpanIds = new Set<string>();
      const collectIds = (items: (AnySpan & { children?: AnySpan[] })[]) => {
        items.forEach((span) => {
          allSpanIds.add(span.spanId);
          if (span.children) {
            collectIds(span.children as (AnySpan & { children?: AnySpan[] })[]);
          }
        });
      };
      collectIds(spanTree);
      setExpandedSpans(allSpanIds);
    }
  }, [spanTree]);

  const toggleSpan = (spanId: string) => {
    setExpandedSpans((prev) => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const handleSpanClick = (span: AnySpan) => {
    setSelectedSpanId(span.spanId);
    if (!spanDetailCache[span.spanId] && agentId) {
      dispatch(getSpanDetail({ agentId, sessionId, spanId: span.spanId }));
    }
  };

  const selectedFullSpan = selectedSpanId ? spanDetailCache[selectedSpanId] : null;
  const selectedPreviewSpan = selectedSpanId ? spans.find((s) => s.spanId === selectedSpanId) : null;
  const dialogSpan = selectedFullSpan || selectedPreviewSpan;

  const renderSpan = (span: AnySpan & { children?: AnySpan[] }, depth = 0): React.ReactNode => {
    const isExpanded = expandedSpans.has(span.spanId);

    return (
      <View key={span.spanId}>
        <SpanRow
          span={span}
          depth={depth}
          isExpanded={isExpanded}
          onToggle={() => toggleSpan(span.spanId)}
          onSpanClick={handleSpanClick}
        />
        {isExpanded && span.children && span.children.map((child) => renderSpan(child as AnySpan & { children?: AnySpan[] }, depth + 1))}
      </View>
    );
  };

  if (isLoading) {
    return (
      <View className="flex items-center justify-center py-8">
        <Spinner size={24} />
        <Text className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading trace data...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View className="flex flex-col items-center justify-center py-8">
        <Icon
          name="error"
          size={24}
          className="text-red-500 mb-2"
        />
        <Text className="text-sm text-red-600 dark:text-red-400">Failed to load trace data: {error}</Text>
      </View>
    );
  }

  if (spans.length === 0) {
    return (
      <View className="flex flex-col items-center justify-center py-8">
        <Icon
          name="activity"
          size={32}
          className="text-gray-300 dark:text-gray-600 mb-2"
        />
        <Text className="text-sm text-gray-500 dark:text-gray-400">No trace data available for this session.</Text>
        <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">Traces may take a moment to appear after a session ends.</Text>
      </View>
    );
  }

  const totalDuration =
    traceSummary?.durationMs ??
    (spans.length > 0 ? Math.max(...spans.map((s) => new Date(s.endTime).getTime())) - Math.min(...spans.map((s) => new Date(s.startTime).getTime())) : 0);
  const errorCount = spans.filter((s) => {
    const isInterrupted = "interrupted" in s ? s.interrupted === true : false;
    if (isInterrupted) return false;
    if (s.statusCode?.toUpperCase() === "ERROR") return true;
    const vst = "voicerunSpanType" in s ? s.voicerunSpanType : undefined;
    if (vst === "logger" && "preview" in s && typeof s.preview === "string" && s.preview.toLowerCase().startsWith("[error]")) return true;
    return false;
  }).length;

  return (
    <View className="flex flex-col h-full">
      <View className="flex flex-row items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-600">
        <View className="flex flex-row items-center gap-4">
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{traceSummary?.spanCount || spans.length}</span> spans
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{formatDuration(totalDuration)}</span> total
          </Text>
          {errorCount > 0 && (
            <Text className="text-xs text-red-600 dark:text-red-400">
              <span className="font-medium">{errorCount}</span> error{errorCount !== 1 ? "s" : ""}
            </Text>
          )}
        </View>
        <View className="flex flex-row items-center gap-2">
          <button
            onClick={() => {
              const allSpanIds = new Set<string>();
              const collectIds = (items: (AnySpan & { children?: AnySpan[] })[]) => {
                items.forEach((span) => {
                  allSpanIds.add(span.spanId);
                  if (span.children) {
                    collectIds(span.children as (AnySpan & { children?: AnySpan[] })[]);
                  }
                });
              };
              collectIds(spanTree);
              setExpandedSpans(allSpanIds);
            }}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Expand all
          </button>
          <Text className="text-gray-300 dark:text-gray-600">|</Text>
          <button
            onClick={() => setExpandedSpans(new Set())}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Collapse all
          </button>
        </View>
      </View>

      <View className="flex-1 overflow-y-auto">{spanTree.map((span) => renderSpan(span))}</View>

      <Dialog
        isOpen={selectedSpanId !== null}
        onClose={() => setSelectedSpanId(null)}
        title={
          dialogSpan && (
            <View className="flex flex-row items-center gap-3">
              <Icon
                name={getSpanIcon(dialogSpan)}
                size={20}
                className={getSpanIconColor(dialogSpan)}
              />
              <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">{getSpanDisplayName(dialogSpan)}</Text>
            </View>
          )
        }
        className="min-w-[85vw] w-[85vw] min-h-[85vh] h-[85vh]"
        size="xl"
      >
        {selectedSpanId && loadingSpanId === selectedSpanId && (
          <View className="flex items-center justify-center h-full">
            <Spinner size={24} />
            <Text className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading span details...</Text>
          </View>
        )}
        {selectedSpanId && selectedFullSpan && loadingSpanId !== selectedSpanId && <SpanDetailView span={selectedFullSpan} />}
      </Dialog>
    </View>
  );
};
